import { AnalyticsBackend, AnalyticsEvent } from '../AnalyticsService';
import { resolveEventCollectorConfig } from './config';
import { EventCollectorConfig, PersistedEvent, CollectorName } from './types';
import { validate } from './validation';
import { mapToCollectorEvent, selectCollector, initEventMapper } from './event-mapper';
import { storePut, storeGetAll, storeMarkAcked, storeDeleteOldest } from './store';
import { sendBatch, sendBeaconBatch, sendBatchKeepalive } from './http-client';
import { FlushScheduler } from './flush-scheduler';
import { eventTypes } from '@utils/constants.ts';

export class EventCollectorService implements AnalyticsBackend {
  private config: EventCollectorConfig | null;
  private queue: PersistedEvent[] = [];
  private isFlushing = false;
  private scheduler: FlushScheduler;
  // In-memory error guard (errors don't use the durable WAL): dedupe identical
  // message+stack within a window, and hard-cap errors per session.
  private errorSeenAt = new Map<string, number>();
  private errorCount = 0;
  // Only the primary instance (no sourceAppOverride) installs global error handlers
  // and claims un-stamped WAL events from older sessions.
  private readonly isPrimary: boolean;

  constructor(sourceAppOverride?: string) {
    const baseConfig = resolveEventCollectorConfig();
    this.isPrimary = !sourceAppOverride;
    this.config = sourceAppOverride && baseConfig
      ? { ...baseConfig, sourceApp: sourceAppOverride }
      : baseConfig;
    if (!this.config) {
      console.warn('[EventCollector] EVENT_COLLECTOR_BASE_URL not set — events will not be sent');
    }
    this.scheduler = new FlushScheduler(
      () => this.flush(),
      () => this.beaconFlush()
    );
  }

  public init = async (): Promise<void> => {
    if (!this.config) return;

    await initEventMapper();

    try {
      const persisted = await storeGetAll();
      const mySourceApp = this.config?.sourceApp;
      // Each instance only claims its own WAL entries. The primary instance also
      // claims un-stamped entries written by older code that predates this field.
      const mine = persisted.filter((e) =>
        e.sourceApp === mySourceApp || (this.isPrimary && !e.sourceApp)
      );
      if (mine.length > 0) {
        this.queue = [...mine, ...this.queue];
        this.enforceCap();
      }
    } catch (err) {
      console.warn('[EventCollector] failed to load persisted events', err);
    }

    if (this.isPrimary) {
      this.installErrorHandlers();
    }

    this.scheduler.start(this.config.flushIntervalMs, this.config.initialFlushDelayMs);
  };

  // Capture uncaught page exceptions and unhandled rejections and route them to the
  // errors collector. Handlers must never throw, or window.onerror would loop.
  private installErrorHandlers(): void {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;

    window.addEventListener('error', (e: ErrorEvent) => {
      try {
        this.reportError({
          errorType: 'js_error',
          errorMessage: e.message || String(e.error) || 'Unknown error',
          stackTrace: e.error instanceof Error ? e.error.stack : undefined,
          source: 'window.onerror',
        });
      } catch {/* reporter must never throw */}
    });

    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      try {
        const reason = e.reason;
        this.reportError({
          errorType: 'js_error',
          errorMessage: reason instanceof Error ? reason.message : String(reason),
          stackTrace: reason instanceof Error ? reason.stack : undefined,
          source: 'window.onunhandledrejection',
          errorSubtype: 'unhandled_rejection',
        });
      } catch {/* reporter must never throw */}
    });
  }

  private reportError(params: {
    errorType: 'js_error' | 'api_error' | 'unknown';
    errorMessage: string;
    stackTrace?: string;
    source?: string;
    errorSubtype?: string;
  }): void {
    this.send({ name: eventTypes.ERROR, params });
  }

  // Returns false if this error should be dropped (duplicate within the dedupe window
  // or over the per-session cap). Best-effort and exception-safe.
  private allowError(message: string | undefined, stack: string | undefined): boolean {
    if (!this.config) return false;
    if (this.errorCount >= this.config.maxErrorsPerSession) return false;

    const now = Date.now();
    const key = `${message ?? ''}::${stack ?? ''}`;
    const lastSeen = this.errorSeenAt.get(key);
    if (lastSeen !== undefined && now - lastSeen < this.config.errorDedupeWindowMs) {
      return false;
    }
    this.errorSeenAt.set(key, now);
    this.errorCount += 1;
    return true;
  }

  public send = (event: AnalyticsEvent): void => {
    if (!this.config) return;

    const clientEventId = crypto.randomUUID();
    const collectorEvent = mapToCollectorEvent(event);
    collectorEvent.properties = { ...collectorEvent.properties, client_event_id: clientEventId };

    if (!validate(collectorEvent)) {
      console.warn('[EventCollector] invalid event dropped', collectorEvent);
      return;
    }

    const collector = selectCollector(event);

    if (collector === 'errors' && !this.allowError(collectorEvent.error_message, collectorEvent.stack_trace)) {
      return;
    }

    const persisted: PersistedEvent = {
      id: clientEventId,
      collector,
      sourceApp: this.config.sourceApp,
      event: collectorEvent,
      retryCount: 0,
      persistedAt: Date.now(),
    };

    this.queue.push(persisted);
    this.enforceCap();

    storePut(persisted).catch((err) => console.warn('[EventCollector] WAL write failed', err));

    if (this.queue.length >= this.config.batchSizeThreshold) {
      this.flush().catch((err) => console.warn('[EventCollector] threshold flush error', err));
    }
  };

  private enforceCap(): void {
    const excess = this.queue.length - this.config!.maxQueueSize;
    if (excess > 0) {
      this.queue.splice(0, excess);
      storeDeleteOldest(excess).catch(() => {/* best effort */});
    }
  }

  private async flush(): Promise<void> {
    if (!this.config || this.isFlushing || this.queue.length === 0) return;
    this.isFlushing = true;

    const batch = this.queue.splice(0, this.queue.length);

    const byCollector = new Map<CollectorName, PersistedEvent[]>();
    for (const item of batch) {
      const list = byCollector.get(item.collector) ?? [];
      list.push(item);
      byCollector.set(item.collector, list);
    }

    for (const [collector, items] of byCollector.entries()) {
      try {
        await sendBatch(collector, items.map((i) => i.event), this.config!);
        await storeMarkAcked(items.map((i) => i.id));
      } catch (err) {
        console.warn('[EventCollector] batch send failed, re-queuing', err);
        this.queue.unshift(...items);
      }
    }

    this.isFlushing = false;
  }

  private beaconFlush(): void {
    if (!this.config || this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.queue.length);

    const byCollector = new Map<CollectorName, PersistedEvent[]>();
    for (const item of batch) {
      const list = byCollector.get(item.collector) ?? [];
      list.push(item);
      byCollector.set(item.collector, list);
    }

    for (const [collector, items] of byCollector.entries()) {
      // Chunk into ≤50-event batches to stay under sendBeacon's ~64 kB limit
      for (let i = 0; i < items.length; i += 50) {
        const chunk = items.slice(i, i + 50);
        const events = chunk.map((it) => it.event);
        // Errors go via keepalive fetch, not sendBeacon: beacon can't set the
        // Authorization/application-json headers (it would force a preflight it
        // can't satisfy) and the collector would silently drop the batch.
        if (collector === 'errors') {
          sendBatchKeepalive(collector, events, this.config!);
          continue;
        }
        const sent = sendBeaconBatch(collector, events, this.config!);
        if (!sent) {
          sendBatchKeepalive(collector, events, this.config!);
        }
      }
    }
    // IDB events are intentionally left until the next session confirms delivery
  }
}
